import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Stack, Typography } from '@mui/material';
import { useShallow } from 'zustand/react/shallow';

import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { FloorplanDesignSelector } from '@/modules/floorplanDesign/store/selectors';
import { OverflowTypography } from '@common/components/OverflowTypography';
import { useAppMetaStore } from '@/modules/floorplanV2/store/project/projectState';
import { IsLatestRevisionSelector } from '@/selectors';
import { logger } from '@/modules/common/logging/logger';

const UNSAVED_CHARACTER = '*';
const EMPTY_VERSION_CHARACTER = '-';

export const Title = () => {
  const { t } = useTranslation(['interface', 'common']);
  const { activeProject, activeLayoutAlternative } = useAppMetaStore(
    useShallow((state) => ({
      activeProject: state.activeProject,
      activeLayoutAlternative: state.activeLayoutAlternative,
    })),
  );
  const { version, revisionIsInDraft, revisionIsLoaded, revisionId } = useFloorplanDesignStore(
    useShallow((state) => ({
      version: state.metaData.version,
      revisionIsInDraft: FloorplanDesignSelector.revisionIsInDraft(state),
      revisionIsLoaded: FloorplanDesignSelector.revisionIsLoaded(state),
      revisionId: state.metaData.revisionId,
    })),
  );
  const unsavedStar = useMemo(
    () => (revisionIsLoaded && revisionIsInDraft ? UNSAVED_CHARACTER : ''),
    [revisionIsLoaded, revisionIsInDraft],
  );
  const readonlySuffix = useMemo(() => {
    if (!revisionIsLoaded) {
      return '';
    }

    if (!activeLayoutAlternative.data) {
      logger.error('Expected activeLayoutAlternative.data to be defined');
      return '';
    }

    const activeRevisionIsLatestRevision = IsLatestRevisionSelector.logic(
      activeLayoutAlternative.data.lastRevisionId,
      revisionId,
    );

    if (activeRevisionIsLatestRevision) {
      return '';
    }

    return ` (${t('interface:versioning.readonly')})`;
  }, [t, revisionId, revisionIsLoaded, activeLayoutAlternative.data]);

  if (activeProject.status !== 'loaded') {
    logger.debug('Expected activeProject to be loaded. Returning null for Title component.', {
      status: activeProject.status,
    });
    return null;
  }

  if (activeLayoutAlternative.status !== 'loaded') {
    logger.debug(
      'Expected activeLayoutAlternative to be loaded. Returning null for Title component.',
      {
        status: activeLayoutAlternative.status,
      },
    );
    return null;
  }

  const activeProjectName = activeProject.data.name;
  const activeLayoutAlternativeName = activeLayoutAlternative.data.name;

  return (
    <Stack
      sx={{
        fontSize: '14px',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'row',
        gap: '6px',
        justifyContent: 'center',
        maxWidth: '100%',
      }}
      divider={<span>/</span>}
    >
      <OverflowTypography sx={{ fontSize: '14px', minWidth: '50px' }}>
        <Link
          href={`/${activeProject.data.id}`}
          sx={{
            textDecoration: 'none',
            color: 'inherit',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {activeProjectName}
        </Link>
      </OverflowTypography>
      {activeLayoutAlternativeName && (
        <OverflowTypography sx={{ fontSize: '14px', minWidth: '50px' }}>
          {activeLayoutAlternativeName}
        </OverflowTypography>
      )}
      <Typography sx={{ fontSize: '14px', minWidth: 'fit-content' }}>
        {revisionIsLoaded &&
          `${t('interface:versioning.version')} ${version || EMPTY_VERSION_CHARACTER}`}
        {readonlySuffix}
        {unsavedStar}
      </Typography>
    </Stack>
  );
};
